import React from 'react';
import { filter, find, get, isEmpty, map, omit, trim } from 'lodash';
import classnames from 'classnames';
import { Button } from 'new/elements/Button';
import { CircularProgress } from '@mui/material';
import { TextField } from 'new/elements/TextField';
import { NavScroller } from 'new/elements/NavScroller';
import { NavScrollerContext } from 'new/elements/NavScroller/NavScroller';
import { MetaInfoInputForm } from 'new/components/MetaInfoInputForm/MetaInfoInputForm';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import UIUtils from 'js/lib/UIUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import DataPathSuggestions from './DataPathSuggestions';
import { formatCode, stringifyObject } from 'js/lib/FormatUtils';
import { FormConfigurationStep } from 'new/components/FormConfigurationStep/FormConfigurationStep';
import MonitoringInterval from 'js/components/data_sources/add_ds_form/MonitoringInterval/MonitoringInterval';

import { ReactComponent as AddIcon } from 'new/components/icons/add-filled-round.svg';
import { ReactComponent as DeleteIcon } from 'new/components/icons/close-cross-filled-round.svg';

import styles from './ConfigureSOAP.module.scss';

const navOptions = [
  { label: 'Configure', id: 'soap-configure' },
  { label: 'Schedule Ingestion', id: 'soap-scheduling' },
];

class ConfigureSOAP extends React.Component {
  constructor(props) {
    super(props);

    this._mapWSDLEntityToOption = this._mapWSDLEntityToOption.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getNewConfig = this._getNewConfig.bind(this);
    this._getSelectField = this._getSelectField.bind(this);
    this._getDataPathField = this._getDataPathField.bind(this);
    this._getPathMatches = this._getPathMatches.bind(this);
    this._getCallResponse = this._getCallResponse.bind(this);
    this._handleCronChanged = this._handleCronChanged.bind(this);
    this._handleLoadWSDLClicked = this._handleLoadWSDLClicked.bind(this);
    this._handleLoadWSDLKeyDown = this._handleLoadWSDLKeyDown.bind(this);
    this._handleLoadOperationClicked = this._handleLoadOperationClicked.bind(this);
    this._handleParamChanged = this._handleParamChanged.bind(this);
    this._handleAddParamClicked = this._handleAddParamClicked.bind(this);
    this._handleDeleteParamClicked = this._handleDeleteParamClicked.bind(this);
    this._handleDataPathSuggestionClicked = this._handleDataPathSuggestionClicked.bind(this);
  }

  componentDidMount() {
    if (this.props.soapConfig['soap.wsdl.url']) {
      this.props.onLoadWSDL();
    }
  }

  componentDidUpdate(prevProps) {
    // if WSDL changed, reset WSDL-dependent fields
    if (
      prevProps.probeSample &&
      get(prevProps.probeSample, 'wsdl') !== get(this.props.probeSample, 'wsdl')
    ) {
      this.props.onResetSOAPConfig();
    }

    // if call response changed or data path changed, validate data path
    const nextCallResponse = this._getCallResponse(this.props.probeSample);

    if (
      this.props.soapConfig['response.data.path'] &&
      nextCallResponse &&
      (this.props.soapConfig['response.data.path'] !== prevProps.soapConfig['response.data.path'] ||
        nextCallResponse !== this._getCallResponse(prevProps.probeSample))
    ) {
      this.props.validateDataPath(
        this.props.soapConfig['response.data.path'],
        this.props.probeSample,
      );
    }
  }

  render() {
    const {
      meta,
      soapConfig,
      probeSample,
      dataPathValidationResult,
      isLoadingWSDL,
      isLoadingOperation,
      sourceConfigFields,
      name = '',
      description = '',
    } = this.props;

    const wsdl = get(probeSample, 'wsdl');
    let selectedService;
    let selectedBinding;
    let selectedOperation;

    if (wsdl) {
      const selectedServiceName = soapConfig['soap.service'];
      selectedService = selectedServiceName
        ? find(wsdl.services, { name: selectedServiceName })
        : null;

      const selectedBindingName = soapConfig['soap.binding'];
      selectedBinding =
        selectedBindingName && selectedService
          ? find(selectedService.bindings, { name: selectedBindingName })
          : null;

      const selectedOperationName = soapConfig['soap.operation'];
      selectedOperation =
        selectedOperationName && selectedBinding
          ? find(selectedBinding.operations, { name: selectedOperationName })
          : null;
    }

    const callResult = get(probeSample, 'callResult');
    let editorContent = '';
    let editorContentType = 'application/xml';
    let callError = get(probeSample, 'callError');
    let soapResponse;

    if (callResult) {
      const { response, statusCode, errorMessage } = callResult;

      if (statusCode >= 400) {
        callError = errorMessage;
        editorContent = response || '';
      } else {
        callError = null;
        editorContent = response;
        soapResponse = response;

        const pathValidationFailed = dataPathValidationResult && !dataPathValidationResult.status;

        if (soapConfig['response.data.path'] && !pathValidationFailed) {
          const pathMatches = this._getPathMatches();

          if (pathMatches !== null) {
            editorContent = pathMatches;
          }
        }
      }
    }

    editorContent = formatCode(editorContent, editorContentType);

    const { visibleFieldData } = this.props;
    const configFields = sourceConfigFields[meta.connectionCategory];
    const cronField = configFields.find((field) => field.name === 'start.cron');

    return (
      <FormConfigurationStep
        connectionName={meta.displayName}
        formContainerClassname={styles.FormConfigurationStepInnerContainer}
        connectionIcon={<img src={meta.iconSm} alt={meta.displayName} width={15} height={15} />}
        headerChildren={
          <MetaInfoInputForm
            name={name}
            description={description}
            onChange={this.props.onMetaInfoChanged}
          />
        }
      >
        <NavScroller navOptions={navOptions}>
          <NavScrollerContext.Consumer>
            {(consumer) => {
              return (
                <div className={classnames([styles.container, 'soap-container'])}>
                  <div id="soap-configure" className={styles.page}>
                    <div className={styles.configure_soap}>
                      <section
                        className={classnames({
                          [styles.section]: true,
                          [styles.section_focused]: consumer?.activeScroll === 'soap-configure',
                        })}
                      >
                        <ul aria-label="main-list">
                          <Row
                            label="WSDL URL"
                            inputChildren={
                              <>
                                {this._getTextField('soap.wsdl.url', {
                                  onKeyDown: this._handleLoadWSDLKeyDown,
                                })}

                                <Button
                                  color="secondary"
                                  variant="outlined"
                                  onClick={this._handleLoadWSDLClicked}
                                >
                                  {isLoadingWSDL ? (
                                    <CircularProgress color="secondary" size={20} />
                                  ) : (
                                    'Fetch'
                                  )}
                                </Button>
                              </>
                            }
                          />

                          {wsdl && (
                            <Row
                              label="Service"
                              inputChildren={this._getSelectField(
                                'soap.service',
                                map(wsdl.services, this._mapWSDLEntityToOption),
                              )}
                            />
                          )}

                          {selectedService && (
                            <Row
                              label="Binding"
                              inputChildren={this._getSelectField(
                                'soap.binding',
                                map(selectedService.bindings, this._mapWSDLEntityToOption),
                              )}
                            />
                          )}
                          {selectedService && (
                            <Row
                              label="Service Port"
                              inputChildren={this._getSelectField(
                                'soap.service.port',
                                map(selectedService.ports, this._mapWSDLEntityToOption),
                              )}
                            />
                          )}

                          {selectedBinding && (
                            <Row
                              label="Operation"
                              inputChildren={
                                <div className={styles.row_centered}>
                                  {this._getSelectField(
                                    'soap.operation',
                                    map(selectedBinding.operations, this._mapWSDLEntityToOption),
                                  )}

                                  <Button
                                    color="secondary"
                                    variant="outlined"
                                    disabled={!selectedOperation}
                                    onClick={this._handleLoadOperationClicked}
                                  >
                                    {isLoadingOperation ? (
                                      <CircularProgress color="secondary" size={20} />
                                    ) : (
                                      'Test'
                                    )}
                                  </Button>
                                </div>
                              }
                            />
                          )}

                          {selectedOperation && (
                            <li>
                              {!isEmpty(soapConfig.params) && (
                                <label className={styles.input_label}>Parameters</label>
                              )}

                              <ol aria-label="parameters-list">
                                {map(soapConfig.params, ({ path, value }, index) => {
                                  const paramOptions = map(
                                    // only show an operation param if it's the selected one
                                    // or if it is not already set
                                    filter(
                                      selectedOperation.sortedParams,
                                      (operationParam) =>
                                        operationParam.path === path ||
                                        !find(
                                          soapConfig.params,
                                          (p) => operationParam.path === p.path,
                                        ),
                                    ),
                                    (operationParam) => ({
                                      label: `${operationParam.path} ${
                                        operationParam.required ? '*' : ''
                                      }`,
                                      value: operationParam.path,
                                    }),
                                  );
                                  const selectedParamOption = getCurrentSelectedOption(
                                    paramOptions,
                                    path,
                                  );
                                  const operationParam = find(selectedOperation.sortedParams, {
                                    path,
                                  });
                                  const { type, possibleValues } = operationParam;

                                  let paramValueControl;

                                  switch (type) {
                                    case 'enum':
                                      const options = map(possibleValues, (value) => ({
                                        label: value,
                                        value: value,
                                      }));
                                      const currentValue = getCurrentSelectedOption(options, value);

                                      paramValueControl = (
                                        <SortedSelect
                                          value={currentValue}
                                          options={options}
                                          components={{
                                            Input: SelectInput,
                                            Option: SelectOption,
                                          }}
                                          isClearable={false}
                                          backspaceRemovesValue={false}
                                          isSearchable={true}
                                          onChange={(currValue) => {
                                            const newValue = getNewSelectionValue(currValue, true);
                                            this._handleParamChanged(path, newValue, index);
                                          }}
                                        />
                                      );
                                      break;

                                    default:
                                      paramValueControl = (
                                        <TextField
                                          id={path}
                                          value={value}
                                          debounceTimeMs={200}
                                          onChange={(event) =>
                                            this._handleParamChanged(
                                              path,
                                              trim(event.target.value),
                                              index,
                                            )
                                          }
                                        />
                                      );
                                  }

                                  return (
                                    <li key={index}>
                                      <div className={styles.row_param_container}>
                                        <SortedSelect
                                          variant="default"
                                          value={selectedParamOption}
                                          options={paramOptions}
                                          components={{
                                            Input: SelectInput,
                                            Option: SelectOption,
                                          }}
                                          isClearable={false}
                                          backspaceRemovesValue={false}
                                          isSearchable={true}
                                          onChange={(currValue) => {
                                            const newValue = getNewSelectionValue(currValue, true);
                                            this._handleParamChanged(newValue, '', index);
                                          }}
                                        />

                                        {paramValueControl}

                                        <DeleteIcon
                                          data-index={index}
                                          className="delete-param-button"
                                          onClick={this._handleDeleteParamClicked}
                                        />
                                      </div>
                                    </li>
                                  );
                                })}
                              </ol>
                              <Button
                                color="info"
                                variant="outlined"
                                onClick={() =>
                                  this._handleAddParamClicked(selectedOperation.sortedParams)
                                }
                              >
                                <AddIcon />
                                Add Parameter
                              </Button>
                            </li>
                          )}
                          {selectedOperation && (
                            <li>
                              {this._getDataPathField('response.data.path', 'Path to data')}
                              <DataPathSuggestions
                                content={soapResponse}
                                contentType={editorContentType}
                                onSuggestionClicked={this._handleDataPathSuggestionClicked}
                              />
                            </li>
                          )}
                        </ul>
                      </section>
                    </div>

                    {editorContent && (
                      <div className={styles.response_soap}>
                        <span className={styles.title}>
                          SOAP Response
                          {callError ? (
                            <span className={styles.error_message}>{callError}</span>
                          ) : null}
                        </span>
                        <LoadableMonacoEditor
                          language="xml"
                          theme="dark"
                          width="100%"
                          height="100%"
                          value={editorContent}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div id="soap-scheduling">
                    <MonitoringInterval
                      field={cronField}
                      source={this.props.sourceBeingEdited}
                      monitorIntervalContainerClassname={classnames({
                        [styles.frequency_container]: true,
                        [styles.frequency_container_focused]:
                          consumer?.activeScroll === 'soap-scheduling',
                      })}
                      config={visibleFieldData}
                      onConfigChanged={(data, name, value) => {
                        this.props.onSOAPConfigChanged(name, value, data);
                      }}
                    />
                  </div>
                </div>
              );
            }}
          </NavScrollerContext.Consumer>
        </NavScroller>
      </FormConfigurationStep>
    );
  }

  _getNewConfig = (newConfig) => {
    let { sourceConfigFields, selectedConnection } = this.props;
    let allFields = sourceConfigFields[selectedConnection];
    let notVisibleFieldNames = map(
      filter(allFields, (field) => !UIUtils.shouldRenderField(field, newConfig)),
      (field) => field.name,
    );

    return omit(newConfig, notVisibleFieldNames);
  };

  _mapWSDLEntityToOption({ name }) {
    return {
      label: name,
      value: name,
    };
  }

  _getTextField(name, props = {}) {
    const { soapConfig, onSOAPConfigChanged } = this.props;

    return (
      <TextField
        id={name}
        fullWidth
        value={get(soapConfig, name, '')}
        onChange={(event) => onSOAPConfigChanged(name, trim(event.target.value))}
        {...props}
      />
    );
  }

  _getSelectField(name, options) {
    const { soapConfig, onSOAPConfigChanged } = this.props;
    const currentValue = getCurrentSelectedOption(options, soapConfig[name]);

    return (
      <SortedSelect
        variant="default"
        value={currentValue}
        components={{ Input: SelectInput, Option: SelectOption }}
        options={options}
        isClearable={false}
        backspaceRemovesValue={false}
        isSearchable={true}
        onChange={(currValue) => {
          const newValue = getNewSelectionValue(currValue, true);
          onSOAPConfigChanged(name, newValue);
        }}
      />
    );
  }

  _getDataPathField(fieldName, label) {
    const { soapConfig, probeSample, dataPathValidationResult } = this.props;
    const dataPath = soapConfig[fieldName];
    const pathValidationFailed = dataPathValidationResult && !dataPathValidationResult.status;
    const message = dataPathValidationResult && dataPathValidationResult.message;
    const soapResponse = this._getCallResponse(probeSample);

    return (
      <div className="input-row">
        <div className="input-holder">
          <div className="data-path-validation-result-holder">
            <label className={styles.input_label} htmlFor={fieldName}>
              {label} (XPath expression)
            </label>
            {soapResponse && dataPathValidationResult && dataPath && (
              <div
                className={classnames('data-path-validation-result', {
                  'invalid-data-path': pathValidationFailed,
                })}
              >
                {message}
              </div>
            )}
          </div>
          {this._getTextField(fieldName)}
        </div>
      </div>
    );
  }

  _getPathMatches() {
    const { dataPathValidationResult } = this.props;
    const matches = get(dataPathValidationResult, 'matches');

    if (!matches) {
      return null;
    }

    return stringifyObject(matches, 'application/xml');
  }

  _getCallResponse(probeSample) {
    const callResult = get(probeSample, 'callResult');

    if (callResult) {
      const { response, statusCode } = callResult;

      if (statusCode < 400) {
        return response;
      }
    }

    return null;
  }

  _handleCronChanged(value) {
    this.props.onSOAPConfigChanged('start.cron', value);
  }

  _handleLoadWSDLClicked() {
    this.props.onLoadWSDL();
  }

  _handleLoadWSDLKeyDown(event) {
    if (event.keyCode === 13) {
      this._handleLoadWSDLClicked();
    }
  }

  _handleLoadOperationClicked() {
    this.props.onLoadOperation();
  }

  _handleParamChanged(name, value, index) {
    const {
      soapConfig: { params },
      onSOAPConfigChanged,
    } = this.props;
    const newSoapParams = [...params];
    newSoapParams[index] = { path: name, value };

    onSOAPConfigChanged('params', newSoapParams);
  }

  _handleAddParamClicked(sortedParams) {
    const {
      soapConfig: { params },
      onSOAPConfigChanged,
    } = this.props;

    // find params which are not already set and take the first one
    const unsetParams = filter(
      sortedParams,
      (param) => !find(params, (p) => p.path === param.path),
    );

    if (unsetParams.length > 0) {
      const { path, type } = unsetParams[0];
      const newSoapParams = [...params, { path, value: type === 'enum' ? null : '' }];
      onSOAPConfigChanged('params', newSoapParams);
    }
  }

  _handleDeleteParamClicked(event) {
    const {
      soapConfig: { params },
      onSOAPConfigChanged,
    } = this.props;
    const { index } = event.currentTarget.dataset;
    const newSoapParams = [...params];
    newSoapParams.splice(index, 1);

    onSOAPConfigChanged('params', newSoapParams);
  }

  _handleDataPathSuggestionClicked(dataPath) {
    this.props.onSOAPConfigChanged('response.data.path', dataPath);
  }
}

export default ConfigureSOAP;

function Row({ label = '', inputChildren = null }) {
  return (
    <li className={styles.input_fetch_container}>
      <label className={styles.input_label} htmlFor="url.template">
        {label}
      </label>

      <div className={styles.input_inner_container}>{inputChildren}</div>
    </li>
  );
}
